import type { IntegrationProvider, IntegrationsResponse } from '../../../hooks/useGetIntegrations';

/**
 * Nexsyis is a single org-level GUID rather than a credential list, but it sits
 * in the same catalog so the Connections page can treat every connection the
 * same way: one row on the list, one sub-page with a table behind it.
 */
export type ConnectionId = IntegrationProvider | 'nex';

export interface ConnectionDefinition {
    id: ConnectionId;
    label: string;
    /** Sub-line under the title on the connection's own page. */
    description: string;
    badgeText: string;
    badgeColor: string;
    /** Each credential belongs to one location rather than the whole org. */
    isPerLocation: boolean;
    /**
     * Whether credentials can be added from this page. Existing credentials are
     * never editable or deletable here — changes go through support so a broken
     * login can't be introduced silently.
     *
     * - PartsTrader: saving kicks off the PT location pull + supplier crawl.
     * - Mitchell / OPSTrax / CollisionLink: additive per location.
     * - Nexsyis: additive only while no customer GUID is set.
     * - CCC: view-only, managed during onboarding / IT setup.
     */
    isAdditive: boolean;
}

export const CONNECTIONS: Record<ConnectionId, ConnectionDefinition> = {
    ccc: {
        id: 'ccc',
        label: 'CCC',
        description: 'Organization-level credentials, set up during onboarding.',
        badgeText: 'C',
        badgeColor: 'blue.300',
        isPerLocation: false,
        isAdditive: false,
    },
    mitchell: {
        id: 'mitchell',
        label: 'Mitchell',
        description: 'One login per location, used by downstream order workflows.',
        badgeText: 'M',
        badgeColor: 'orange.400',
        isPerLocation: true,
        isAdditive: true,
    },
    partstrader: {
        id: 'partstrader',
        label: 'PartsTrader',
        description: 'Organization-level credentials covering every location.',
        badgeText: 'PT',
        badgeColor: 'blue.600',
        isPerLocation: false,
        isAdditive: true,
    },
    opstrax: {
        id: 'opstrax',
        label: 'OPSTrax',
        description: 'One login per location.',
        badgeText: 'O',
        badgeColor: 'gray.500',
        isPerLocation: true,
        isAdditive: true,
    },
    collisionlink: {
        id: 'collisionlink',
        label: 'CollisionLink',
        description: 'One login per location.',
        badgeText: 'CL',
        badgeColor: '#C81F3F',
        isPerLocation: true,
        isAdditive: true,
    },
    nex: {
        id: 'nex',
        label: 'Nexsyis',
        description: 'Organization-level connection, identified by a customer GUID.',
        badgeText: 'NEX',
        badgeColor: '#6778BF',
        isPerLocation: false,
        isAdditive: true,
    },
    tesla: {
        id: 'tesla',
        label: 'Tesla',
        description: 'Organization-level credentials.',
        badgeText: 'T',
        badgeColor: 'red.500',
        isPerLocation: false,
        isAdditive: false,
    },
};

export interface ConnectionGroup {
    id: string;
    label: string;
    description: string;
    connectionIds: ConnectionId[];
}

/**
 * Connections grouped by what they do for the shop. CCC and Tesla are
 * deliberately in no group — add one back to a group to surface it again.
 * CCC credentials stay on the integrations payload; they are not listed here.
 */
export const CONNECTION_GROUPS: ConnectionGroup[] = [
    {
        id: 'estimating',
        label: 'Estimating',
        description: 'Where Yaxle reads estimates from.',
        connectionIds: ['mitchell'],
    },
    {
        id: 'marketplaces',
        label: 'Marketplaces',
        description: 'Where Yaxle quotes and orders parts.',
        connectionIds: ['partstrader', 'opstrax', 'collisionlink'],
    },
    {
        id: 'management-systems',
        label: 'Management Systems',
        description: 'Where Yaxle syncs jobs and invoices.',
        connectionIds: ['nex'],
    },
];

export const isConnectionId = (value: string | undefined): value is ConnectionId =>
    !!value && Object.prototype.hasOwnProperty.call(CONNECTIONS, value);

export interface VisibleConnectionsOptions {
    /** Invoicing-only orgs connect Nexsyis and nothing else. */
    invoicingOnly?: boolean;
}

/** Providers the Connections screens are allowed to render. */
export const isListedConnection = (
    id: ConnectionId,
    options?: VisibleConnectionsOptions,
): boolean => {
    if (options?.invoicingOnly) return id === 'nex';
    return CONNECTION_GROUPS.some((group) => group.connectionIds.includes(id));
};

export interface CredentialRow {
    _id: string;
    username: string;
    password: string | null;
    locationId?: string | null;
    locationTitle?: string | null;
}

/** Credentials stored for a connection. Nexsyis has none — it stores a GUID. */
export const getCredentialRows = (
    id: ConnectionId,
    data: IntegrationsResponse,
): CredentialRow[] => {
    switch (id) {
        case 'nex':
            return [];
        case 'mitchell':
        case 'opstrax':
        case 'collisionlink':
            return data[id].map((credential) => ({
                _id: credential._id,
                username: credential.username,
                password: credential.password,
                locationId: credential.locationId,
                locationTitle: credential.locationTitle,
            }));
        default:
            return data[id].map((credential) => ({
                _id: credential._id,
                username: credential.username,
                password: credential.password,
            }));
    }
};

export interface ConnectionStatus {
    isConnected: boolean;
    /** Distinct locations with a credential. Null for org-level connections. */
    locationCount: number | null;
    totalLocations: number;
}

export const getConnectionStatus = (
    id: ConnectionId,
    data: IntegrationsResponse,
): ConnectionStatus => {
    const totalLocations = data.locations.length;

    if (id === 'nex') {
        return {
            isConnected: !!data.nex?.customerGuid,
            locationCount: null,
            totalLocations,
        };
    }

    const rows = getCredentialRows(id, data);
    const locationCount = CONNECTIONS[id].isPerLocation
        ? new Set(rows.map((row) => row.locationId).filter(Boolean) as string[]).size
        : null;

    return {
        isConnected: rows.length > 0,
        locationCount,
        totalLocations,
    };
};

/** Right-hand meta line on a connection row: how much coverage it has today. */
export const describeCoverage = (status: ConnectionStatus): string | null => {
    if (!status.isConnected) return null;
    // Org-level connections aren't scoped to a location, so a single credential
    // already covers the whole org — there is no "n of m" to report.
    if (status.locationCount === null) return 'All locations';
    return `${status.locationCount} of ${status.totalLocations} location${
        status.totalLocations === 1 ? '' : 's'
    }`;
};

/**
 * Nexsyis is only relevant to orgs that run on it: surface it when the
 * management system says so or a connection already exists.
 */
export const shouldShowNex = (data: IntegrationsResponse): boolean =>
    data.nex?.managementSystem === 'nex' || !!data.nex?.customerGuid;

/**
 * Groups to render on the Connections list. Nexsyis stays hidden for other
 * orgs until they run on it; invoicing-only orgs always get that one row.
 */
export const getVisibleConnectionGroups = (
    data: IntegrationsResponse,
    options?: VisibleConnectionsOptions,
): ConnectionGroup[] =>
    CONNECTION_GROUPS.map((group) => ({
        ...group,
        connectionIds: group.connectionIds.filter((id) => {
            if (options?.invoicingOnly) return id === 'nex';
            return id !== 'nex' || shouldShowNex(data);
        }),
    })).filter((group) => group.connectionIds.length > 0);
